import { USERS_SETTINGS_STORE } from "@/constants/sessionStorage";
import { rawDefaultStateValuesGetter, zustandStorage } from "@/lib/storage";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";
import { create } from "zustand";
import { persist } from "zustand/middleware";

const usersStoreSchema = z.object({
  username: z.string().optional(),
  role: z.string().optional(),
  active: z.boolean().optional(),
  minCreatedAt: z.string().optional(),
  maxCreatedAt: z.string().optional(),
  minLastLoginAt: z.string().optional(),
  maxLastLoginAt: z.string().optional(),
  sortBy: z.string().optional(),
  orderBy: z.string().optional(),
  page: z.number().min(1),
  pageSize: z.number().max(200),
});

export type UsersStore = z.infer<typeof usersStoreSchema>;

const defaults: UsersStore = {
  page: 1,
  pageSize: 20,
};

interface Store {
  state: UsersStore;
  setState: (newState: Partial<UsersStore>) => void;
  reset: () => void;
}

const getRawDefaultValues = rawDefaultStateValuesGetter(USERS_SETTINGS_STORE);

const { data: defaultValues } = usersStoreSchema.safeParse(getRawDefaultValues());

const useStore = create<Store>()(
  persist(
    (set) => ({
      state: defaultValues ?? defaults,

      setState: (newState) =>
        set((s) => {
          const merged = { ...s.state, ...newState };
          const parsed = usersStoreSchema.safeParse(merged);
          return {
            state: parsed.success ? parsed.data : s.state,
          };
        }),

      reset: () => set({ state: defaults }),
    }),
    {
      name: USERS_SETTINGS_STORE,
      onRehydrateStorage: () => (state) => {
        if (!state?.state) return;

        const parsed = usersStoreSchema.safeParse(state.state);

        if (!parsed.success) {
          state.state = defaults;
          return;
        }

        state.state = parsed.data;
      },
      partialize: (state) => state.state,
      storage: zustandStorage,
    },
  ),
);

export const useUsersStore = () => {
  const store = useStore();
  const [params] = useSearchParams();
  const paramsValue = params.get(USERS_SETTINGS_STORE);

  useEffect(() => {
    if (!paramsValue) {
      store.reset();
    }
  }, [paramsValue]);

  return store;
};
